

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewMerchantAssetModal from 'components/NewMerchantAssetModal';
import StatusBadge from "components/StatusBadge";
import { formatDate } from 'utils/DateTimeConversion';
import MonoText from 'components/MonoText';

const MerchantAssets = () => {
    const [assets, setAssets] = useState([]);
    const [merchantsById, setMerchantsById] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('merchant_assets');

    useEffect(() => {
        fetchAssets();
        fetchMerchants();
    }, []);

    const fetchAssets = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/merchant-assets/');
            setAssets(response.data);
        } catch (error) {
            console.error('Error fetching merchant assets:', error);
            setSnackbar({ open: true, message: 'Error loading Merchant Assets', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMerchants = async () => {
        try {
            const response = await axiosInstance.get('/merchant-assets/merchant-options');
            const byId = (response.data || []).reduce((acc, merchant) => {
                acc[merchant.id] = merchant;
                return acc;
            }, {});
            setMerchantsById(byId);
        } catch (error) {
            console.error('Error loading merchants:', error);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveAsset = async (payload) => {
        try {
            await axiosInstance.post('/merchant-assets/', payload);
            setSnackbar({ open: true, message: 'Merchant Asset created successfully', severity: 'success' });
            fetchAssets();
        } catch (error) {
            console.error('Error saving Merchant Asset:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/merchant-assets/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Asset Name",
            accessor: "name",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        { Header: "Asset Type", accessor: "asset_type" },
        {
            Header: "Merchant",
            accessor: "merchant_id",
            Cell: ({ value }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {merchantsById[value]?.name || `#${value}`}
                </MDTypography>
            )
        },
        {
            Header: "Max Cap / Unit",
            accessor: "capacity_per_slot",
            Cell: ({ value }) => (
                <MDTypography variant="caption" color="text">
                    {value !== null ? value : '-'}
                </MDTypography>
            )
        },
        {
            Header: "Geo Location",
            accessor: "geo_location",
            Cell: ({ value }) => (
                <MDTypography variant="caption" color="text">
                    {value || '-'}
                </MDTypography>
            )
        },
        {
            Header: "Status",
            accessor: "active",
            Cell: ({ value }) => (
                <StatusBadge status={value ? "Active" : "Inactive"} />
            )
        },
        {
            Header: "Modified Date",
            accessor: "updated_at",
            width: "10%",
            Cell: ({ value }) => <MonoText>{value ? formatDate(value) : '-'}</MonoText>,
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: assets,
        initialState: {
            sortBy: [
                {
                    id: 'updated_at',
                    desc: true
                }
            ]
        }
    }), [assets, merchantsById]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Merchant Assets</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;New Merchant Asset
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : assets.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                        defaultSortField="updated_at"
                                        defaultSortDirection="desc"
                                    />
                                ) : (
                                    <EmptyState moduleName="Merchant Asset" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewMerchantAssetModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveAsset}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default MerchantAssets;
